import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Send, Lock, Paperclip, X, Sparkles, Loader2, Reply, FileText } from 'lucide-react';
import type { MetaConversation } from '@/lib/stores/metaMessagingStore';
import { useMetaMessagingStore, metaAttachmentType } from '@/lib/stores/metaMessagingStore';
import { acceptAttribute, attachmentProblem, resolveMime } from './metaMessagingRules';
import { supabase } from '@/lib/supabase';
import { windowState, conversationLabel } from './metaConversationHelpers';
import { metaMessageSummary } from './MetaMessageBubble';
import { ResponseWindowTimer, metaWindowPhases, META_WINDOW_EXPIRED } from '@/components/messages/ResponseWindowTimer';
import { tip } from '@/components/messages/ChatTooltip';
import { useMetaQuickReplies } from './useMetaQuickReplies';
import { useOrgAiEnabled, AI_DISABLED_TOOLTIP } from '@/lib/hooks/useOrgAiEnabled';

interface MetaComposerProps {
  conversation: MetaConversation;
  canSend: boolean;
}

// Doc Meta: 1000 byte UTF-8. Il contatore mostra i byte, che con accenti ed emoji
// non coincidono con i caratteri.
const MAX_BYTES = 1000;
const utf8Bytes = (s: string) => new TextEncoder().encode(s).length;
// Formati accettati da Meta (doc Instagram Messaging API): foto PNG/JPEG, audio AAC/M4A/WAV/MP4,
// video MP4/OGG/AVI/MOV/WEBM, PDF; Messenger anche GIF. Vedi metaMessagingRules.ts.

/**
 * Barra di scrittura di Instagram e Messenger, allineata a quella di WhatsApp:
 * testo, allegato (foto, video, audio, PDF), risposta a un messaggio preciso e bozza
 * scritta con l'AI. Rispetta la finestra Meta calcolata dall'ultimo messaggio del
 * contatto: quando e' scaduta il campo si spegne e spiega perche'. Invio ottimistico.
 */
export function MetaComposer({ conversation, canSend }: MetaComposerProps) {
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [aiMode, setAiMode] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  // Interruttore IA dell'azienda: spento = composer IA disattivato.
  const aiEnabled = useOrgAiEnabled();
  const [aiError, setAiError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sendMessage = useMetaMessagingStore((s) => s.sendMessage);
  const sendSenderAction = useMetaMessagingStore((s) => s.sendSenderAction);
  // Errore d'invio di QUESTA conversazione (un allegato partito da un'altra chat resta la').
  const sendError = useMetaMessagingStore((s) => s.sendErrors[conversation.id]);
  const generalError = useMetaMessagingStore((s) => s.error);
  const error = sendError ?? generalError;
  const clearError = useMetaMessagingStore((s) => s.clearError);
  const setSendError = useMetaMessagingStore((s) => s.setSendError);
  const replyTo = useMetaMessagingStore((s) => s.replyTo[conversation.id] ?? null);
  const setReplyTo = useMetaMessagingStore((s) => s.setReplyTo);
  // `tick` forza il ricalcolo dello stato quando il timer cambia fase (24h → operatore umano → scaduta).
  // Dichiarato PRIMA del return anticipato: gli hook devono avere sempre lo stesso ordine.
  const [tick, setTick] = useState(0);

  // Cambio conversazione: campo vuoto, niente file ne' AI rimasti dalla chat precedente.
  useEffect(() => {
    setText('');
    setFile(null);
    setAiMode(false);
    setAiPrompt('');
    setAiError(null);
    const t = setTimeout(() => textareaRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [conversation.id]);

  // Anteprima del file scelto: una sola URL per file, liberata quando cambia.
  const previewUrl = useMemo(
    () => (file && metaAttachmentType(file.type) === 'image' ? URL.createObjectURL(file) : null),
    [file]
  );
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  // Scegliendo "Rispondi" su un messaggio, il cursore va nel campo.
  useEffect(() => {
    if (replyTo) textareaRef.current?.focus();
  }, [replyTo]);

  // Testo mandato dal pannello assistente ("Usa come bozza", "In chat"): va nel campo,
  // in coda a quello gia' scritto, e non parte finche' non premi Invia.
  const pendingDraft = useMetaMessagingStore((s) => s.pendingDraft[conversation.id]);
  const setPendingDraft = useMetaMessagingStore((s) => s.setPendingDraft);
  useEffect(() => {
    if (!pendingDraft) return;
    setText((prev) => (prev.trim() ? `${prev.trimEnd()}\n\n${pendingDraft}` : pendingDraft));
    setAiMode(false);
    setPendingDraft(conversation.id, null);
    // Niente cleanup: svuotare pendingDraft rifa' girare l'effetto e annullerebbe il focus.
    setTimeout(() => textareaRef.current?.focus(), 30);
  }, [pendingDraft, conversation.id, setPendingDraft]);

  // Risposte rapide con "/": stessi modelli e stesso menu di WhatsApp.
  const quick = useMetaQuickReplies({
    organizationId: conversation.organization_id,
    clientId: conversation.client_id ?? null,
    conversationId: conversation.id,
    text,
    setText,
    textareaRef,
  });

  if (!canSend) {
    return (
      <div className="px-4 py-3 border-t border-[rgba(55,53,47,0.09)] dark:border-white/10 text-xs text-[#787774] flex items-center gap-2">
        <Lock className="h-3.5 w-3.5" />
        Puoi leggere questa conversazione ma non rispondere: chiedi il permesso "Rispondi ai messaggi Instagram e Messenger".
      </div>
    );
  }

  void tick;
  const state = windowState(conversation);
  const blocked = state === 'blocked';
  const phases = metaWindowPhases(conversation.last_inbound_at);
  const bytes = utf8Bytes(text);
  const tooLong = bytes > MAX_BYTES;
  const canSubmit = !blocked && !tooLong && (Boolean(text.trim()) || Boolean(file));

  const submit = () => {
    if (!canSubmit) return;
    const value = text.trim();
    const toSend = file;
    quick.close();
    setText('');
    setFile(null);
    // Non si aspetta la risposta: la bolla ottimistica e' gia' nel thread.
    void sendMessage(conversation.id, value, { replyToMid: replyTo?.meta_message_id ?? null, file: toSend });
    textareaRef.current?.focus();
  };

  // Unico controllo per selettore file e incolla: formato accettato dal canale e dimensione.
  const pickFile = (f: File | null) => {
    if (!f) return;
    const problem = attachmentProblem(f, conversation.channel);
    if (problem) {
      setAiError(null);
      setSendError(conversation.id, problem);
      return;
    }
    // File senza tipo dal browser (capita con .m4a): gli si da' quello dedotto dall'estensione,
    // cosi' il caricamento e il tipo Meta (foto/video/audio/file) sono giusti.
    const mime = resolveMime(f);
    setFile(f.type ? f : new File([f], f.name, { type: mime }));
    textareaRef.current?.focus();
  };

  const generate = async () => {
    const prompt = aiPrompt.trim();
    if (!prompt || aiBusy || !aiEnabled) return;
    setAiBusy(true);
    setAiError(null);
    try {
      // Stessa funzione della scrittura AI di WhatsApp, con il nome del contatto.
      const { data, error: fnError } = await supabase.functions.invoke('whatsapp-compose-message', {
        body: {
          organization_id: conversation.organization_id,
          prompt,
          contact_name: conversationLabel(conversation),
          context: {},
        },
      });
      if (fnError) throw fnError;
      const message = (data as { message?: string } | null)?.message;
      if (!message) throw new Error('Nessuna bozza generata');
      setText(message);
      setAiMode(false);
      setAiPrompt('');
      setTimeout(() => textareaRef.current?.focus(), 30);
    } catch {
      setAiError('Bozza non generata, riprova');
    } finally {
      setAiBusy(false);
    }
  };

  const iconBtn =
    'p-1.5 rounded-md transition-colors text-[#A4A4A0] hover:text-[#787774] hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06] disabled:opacity-40';

  return (
    <div className="border-t border-[rgba(55,53,47,0.09)] dark:border-white/10 bg-[#F0F2F5] dark:bg-[#202020]">
      <div className="px-4 pt-2">
        <ResponseWindowTimer phases={phases} expired={META_WINDOW_EXPIRED} onPhaseChange={() => setTick((t) => t + 1)} />
      </div>
      {error && (
        <div className="mx-4 mt-2 px-3 py-2 rounded-md bg-red-50 dark:bg-red-900/20 text-xs text-red-700 dark:text-red-300 flex items-center justify-between gap-2">
          <span>{error}</span>
          <button type="button" onClick={() => clearError(conversation.id)} className="text-red-700 dark:text-red-300 underline">Chiudi</button>
        </div>
      )}

      {replyTo && (
        <div className="mx-4 mt-2 flex items-center gap-2 px-3 py-2 rounded-md bg-white dark:bg-gray-800 border-l-4 border-[#2383e2]">
          <Reply className="h-3.5 w-3.5 text-[#2383e2] flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-medium text-[#2383e2]">
              Rispondi a {replyTo.direction === 'outbound' ? 'te stesso' : conversationLabel(conversation)}
            </div>
            <div className="text-xs text-[#787774] truncate">{metaMessageSummary(replyTo)}</div>
          </div>
          <button type="button" onClick={() => setReplyTo(conversation.id, null)} className={iconBtn} aria-label="Annulla risposta" {...tip('Annulla la risposta (Esc)')}>
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {file && (
        <div className="mx-4 mt-2 flex items-center gap-2 px-3 py-2 rounded-md bg-white dark:bg-gray-800 text-xs">
          {previewUrl ? (
            <img src={previewUrl} alt="" className="h-10 w-10 rounded object-cover" />
          ) : (
            <FileText className="h-5 w-5 text-[#787774]" />
          )}
          <span className="truncate flex-1 text-[#37352F] dark:text-white">{file.name}</span>
          <span className="text-[#a4a4a0]">{(file.size / (1024 * 1024)).toFixed(1)} MB</span>
          <button type="button" onClick={() => setFile(null)} className={iconBtn} aria-label="Togli allegato" {...tip('Togli il file')}>
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {aiMode && (
        <div className="mx-4 mt-2">
          <div className="flex items-center gap-1.5 text-[10px] text-purple-500 font-medium mb-1">
            <Sparkles className="h-3 w-3" /> Scrivi con l'AI: descrivi cosa rispondere, poi controlli la bozza prima di inviarla
          </div>
          <div className="flex items-end gap-2">
            <textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void generate(); }
                if (e.key === 'Escape') { e.preventDefault(); setAiMode(false); }
              }}
              rows={2}
              autoFocus
              placeholder="Es: ringrazia e proponi una chiamata di 15 minuti questa settimana"
              className="flex-1 resize-none px-3 py-2 text-sm rounded-md border border-purple-300 dark:border-purple-700 bg-white dark:bg-[#202020] text-[#37352F] dark:text-white placeholder:text-[#a4a4a0] focus:outline-none focus:border-purple-500"
            />
            <button
              type="button"
              onClick={() => void generate()}
              disabled={!aiPrompt.trim() || aiBusy || !aiEnabled}
              className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-purple-600 text-white text-sm font-medium disabled:opacity-40 hover:bg-purple-700"
              {...tip(aiEnabled ? 'Prepara la bozza (Invio)' : AI_DISABLED_TOOLTIP)}
            >
              {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            </button>
          </div>
          {aiError && <p className="text-[11px] text-red-600 mt-1">{aiError}</p>}
        </div>
      )}

      <div className="relative flex items-end gap-2 px-4 py-3">
        {!aiMode && !blocked && quick.menu}
        <div className="flex items-center gap-0.5 pb-1">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={blocked}
            className={iconBtn}
            aria-label="Allega"
            {...tip(conversation.channel === 'instagram'
              ? 'Allega foto (PNG, JPEG fino a 8 MB), video, audio o PDF'
              : 'Allega foto (PNG, JPEG, GIF fino a 8 MB), video, audio o PDF')}
          >
            <Paperclip className="h-4 w-4" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptAttribute(conversation.channel)}
            className="hidden"
            onChange={(e) => { pickFile(e.target.files?.[0] ?? null); e.target.value = ''; }}
          />
          <button
            type="button"
            onClick={() => setAiMode((v) => !v)}
            disabled={blocked || (!aiEnabled && !aiMode)}
            className={`${iconBtn} ${aiMode ? '!text-purple-600 bg-purple-100 dark:bg-purple-900/30' : ''}`}
            aria-label="Scrivi con l'AI"
            {...tip(aiMode ? 'Torna alla scrittura normale (Esc)' : !aiEnabled ? AI_DISABLED_TOOLTIP : 'Scrivi la risposta con l\'AI')}
          >
            <Sparkles className="h-4 w-4" />
          </button>
        </div>
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            quick.onTextChange(e.target.value, e.target.selectionStart);
            // "Sta scrivendo" verso il contatto: lo store lo limita a uno ogni 20 secondi,
            // e solo entro le 24 ore (fuori il core non lo manda).
            if (e.target.value.trim() && state === 'standard') void sendSenderAction(conversation.id, 'typing_on');
          }}
          onPaste={(e) => {
            const pasted = e.clipboardData?.files?.[0];
            if (pasted) { e.preventDefault(); pickFile(pasted); }
          }}
          onKeyDown={(e) => {
            if (quick.onKeyDown(e)) return;
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
            // Esc chiude prima la risposta in corso, e solo quella.
            if (e.key === 'Escape' && replyTo) {
              e.preventDefault();
              setReplyTo(conversation.id, null);
            }
          }}
          disabled={blocked}
          rows={1}
          placeholder={blocked ? 'Risposta non disponibile: il contatto deve riscriverti' : file ? 'Aggiungi un testo (facoltativo)' : 'Scrivi un messaggio, / per le risposte rapide'}
          className="flex-1 resize-none max-h-40 px-3 py-2 text-sm rounded-lg border border-transparent bg-white dark:bg-[#2a2a2a] text-[#37352F] dark:text-white placeholder:text-[#a4a4a0] focus:outline-none focus:border-[#2383e2] disabled:opacity-60 transition-colors"
        />
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className="h-9 px-3 inline-flex items-center gap-1.5 rounded-lg bg-[#37352F] dark:bg-white text-white dark:text-[#37352F] text-sm font-medium disabled:opacity-40 hover:opacity-90 active:scale-95 transition-all"
          aria-label="Invia"
          {...tip('Invia (Invio). Maiusc+Invio per andare a capo')}
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
      {bytes > MAX_BYTES * 0.8 && (
        <div className={`px-4 pb-2 -mt-2 text-[10px] text-right ${tooLong ? 'text-red-600' : 'text-[#a4a4a0]'}`}>
          {bytes}/{MAX_BYTES}{tooLong ? ' · troppo lungo per Instagram e Messenger' : ''}
        </div>
      )}
    </div>
  );
}
